import * as z from "zod";
import { userFacingError } from "../authorization";
import { customDomainHostname, DOMAIN_MAX_LENGTH } from "../customDomains";
import { zql } from "../generatedDrizzleSchema";
import { assertSpaceDomain, domainConfig, findSetup } from "./customDomains";
import { mutator } from "./mutator";

export const updateCustomDomain = mutator({
  ...domainConfig,
  description: "Changes a custom domain’s scope, availability, or default.",
  interactionType: "update",
  schema: domainConfig.schema.extend({
    spaceId: z.string().nullable().optional(),
    enabled: z.boolean().optional(),
    setAsDefault: z.boolean().optional(),
  }),
  resolve: { space: { kind: "space", from: "spaceId", optional: true } },
  fn: async ({ tx, ctx, args }) => {
    const setup = await findSetup(tx, ctx.tenantId, args);
    if (!setup || setup.status === "removing")
      throw userFacingError("Domain setup not found.");
    if (args.space?.mode === "personal")
      throw userFacingError("Choose a non-personal space.");
    if (args.enabled === true && setup.status !== "ready")
      throw userFacingError(
        "Finish setting up this domain before enabling it."
      );
    if (
      args.setAsDefault &&
      (!(args.enabled ?? setup.enabled) || setup.status !== "ready")
    )
      throw userFacingError(
        "Enable the domain and finish setup before making it the default."
      );
    const spaceId =
      args.spaceId === undefined ? setup.spaceId : (args.space?.id ?? null);
    if (spaceId !== setup.spaceId) assertSpaceDomain(setup.domain, spaceId);
    const hostname = customDomainHostname(setup.domain, spaceId);
    if (hostname !== setup.hostname && hostname.length > DOMAIN_MAX_LENGTH)
      throw userFacingError(`Maximum ${DOMAIN_MAX_LENGTH} characters.`);
    if (args.setAsDefault !== undefined)
      for (const { id } of await tx.run(
        zql.customDomainSetups
          .where("tenantId", ctx.tenantId)
          .where("spaceId", "IS", spaceId)
          .orderBy("id", "asc")
      ))
        await tx.mutate.customDomainSetups.update({ id, preferredAt: null });
    await tx.mutate.customDomainSetups.update({
      id: setup.id,
      spaceId,
      hostname,
      enabled: args.enabled ?? setup.enabled,
      ...(args.setAsDefault !== undefined && {
        preferredAt: args.setAsDefault ? Date.now() : null,
      }),
      ...(hostname !== setup.hostname
        ? {
            managed: true,
            enabled: false,
            status: "pending" as const,
            activatedAt: null,
            dnsRecords: setup.dnsRecords
              .filter(({ purpose }) => purpose === "routing")
              .map((record) => ({
                ...record,
                name: hostname,
                verified: false,
              })),
            lastError: null,
          }
        : {}),
      updatedAt: Date.now(),
    });
  },
});
